import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

// Backend refresh token is valid for 7h. We warn well before that hard
// expiry (at 6h) and force logout 10 min later (6h10m total) so in-flight
// API calls never silently fail against an already-expired session.
const LOGIN_FLAG_KEY = "login";
const LOGIN_TIME_KEY = "loginTime";
const WARNING_AFTER_MS = 6 * 60 * 60 * 1000; // 6 hours
const COUNTDOWN_MS = 10 * 60 * 1000; // 10 minutes
const LOGOUT_AFTER_MS = WARNING_AFTER_MS + COUNTDOWN_MS;
const CHECK_INTERVAL_MS = 1000;

const formatClockTime = (timestamp) =>
  new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const formatCountdown = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const SessionTimeoutWatcher = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [remainingMs, setRemainingMs] = useState(COUNTDOWN_MS);
  const [logoutClockTime, setLogoutClockTime] = useState("");
  const dismissedRef = useRef(false);

  const forceLogout = () => {
    localStorage.clear();
    setShowModal(false);
    dismissedRef.current = false;
    navigate("/");
  };

  useEffect(() => {
    const tick = () => {
      if (localStorage.getItem(LOGIN_FLAG_KEY) !== "true") {
        dismissedRef.current = false;
        setShowModal(false);
        return;
      }

      let loginTime = Number(localStorage.getItem(LOGIN_TIME_KEY));
      if (!loginTime) {
        loginTime = Date.now();
        localStorage.setItem(LOGIN_TIME_KEY, String(loginTime));
      }

      const elapsed = Date.now() - loginTime;

      if (elapsed >= LOGOUT_AFTER_MS) {
        forceLogout();
      } else if (elapsed >= WARNING_AFTER_MS) {
        setRemainingMs(loginTime + LOGOUT_AFTER_MS - Date.now());
        setLogoutClockTime(formatClockTime(loginTime + LOGOUT_AFTER_MS));
        if (!dismissedRef.current) {
          setShowModal(true);
        }
      } else {
        setShowModal(false);
      }
    };

    tick();
    const intervalId = setInterval(tick, CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = () => {
    dismissedRef.current = true;
    setShowModal(false);
  };

  return (
    <Dialog
      open={showModal}
      onClose={handleDismiss}
      aria-labelledby="session-timeout-title"
    >
      <DialogTitle id="session-timeout-title">
        Your session is about to expire
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          For your security, you will be automatically logged out at{" "}
          <strong>{logoutClockTime}</strong> ({formatCountdown(remainingMs)}{" "}
          remaining). Save your work if needed.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss}>Dismiss</Button>
        <Button onClick={forceLogout} variant="contained" color="error">
          Logout Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWatcher;
